import React from 'react';
import { X, ArrowUpDown, LayoutGrid, LayoutList, Table2, Bookmark } from 'lucide-react';
import {
  FACETS, SORTS, GROUP_BY, facetCounts, countActiveFilters,
  savedViews, isViewActive,
} from '../utils/labFilters';

const selectStyle = {
  fontFamily: 'inherit', fontSize: 13, padding: '5px 9px', borderRadius: 6,
  background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
  border: '1px solid var(--border-primary)',
};

const VIEWS = [
  { value: 'cards', label: 'Cards', Icon: LayoutGrid },
  { value: 'rows', label: 'Rows', Icon: LayoutList },
  { value: 'table', label: 'Compact', Icon: Table2 },
];

/**
 * Faceted filter bar for the Labs page.
 *
 * Two deliberate behaviours:
 *  - every pill carries a live count, so the shape of the data is visible
 *    before committing to a click;
 *  - a zero-count pill is DISABLED rather than removed, so the bar never
 *    reflows under the cursor mid-click.
 */

const toneStyles = {
  success: { bg: 'var(--badge-success-bg)', fg: 'var(--accent-success)', border: 'var(--accent-success)' },
  warning: { bg: 'var(--badge-warning-bg)', fg: 'var(--accent-warning)', border: 'var(--accent-warning)' },
  critical: { bg: 'var(--badge-danger-bg)', fg: 'var(--accent-danger)', border: 'var(--accent-danger)' },
  default: { bg: 'var(--badge-info-bg)', fg: 'var(--accent-primary)', border: 'var(--accent-primary)' },
};

function FilterPill({ label, count, pressed, disabled, tone = 'default', onToggle }) {
  const t = toneStyles[tone] || toneStyles.default;
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onToggle}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '5px 11px', borderRadius: 999, cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
        background: pressed ? t.bg : 'var(--bg-tertiary)',
        color: pressed ? t.fg : 'var(--text-secondary)',
        border: `1px solid ${pressed ? t.border : 'var(--border-primary)'}`,
        opacity: disabled ? 0.4 : 1,
        transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
      }}
    >
      <span>{label}</span>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 11, fontVariantNumeric: 'tabular-nums',
        minWidth: 18, textAlign: 'center', padding: '1px 5px', borderRadius: 999,
        background: pressed ? t.fg : 'var(--bg-secondary)',
        color: pressed ? 'var(--bg-primary)' : 'var(--text-muted)',
      }}>
        {count}
      </span>
    </button>
  );
}

export default function LabFilterBar({
  labs, active, search, sort, now,
  onToggleFacet, onClear, onSortChange,
  resultCount,
  view, onViewChange,
  groupBy, onGroupByChange,
  currentOwnerName, onApplyView,
}) {
  const activeCount = countActiveFilters(active);
  const views = savedViews(currentOwnerName);

  return (
    <div
      style={{
        // Sticky so the controls stay reachable once the list is long.
        position: 'sticky', top: 0, zIndex: 5,
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 12, padding: 16, marginBottom: 20,
      }}
    >
      {/* Saved views — one-click starting points. Each is just a filter state,
          so a preset and a hand-built filter produce the same shareable URL. */}
      <div
        role="group"
        aria-label="Saved views"
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
          paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid var(--border-primary)',
        }}
      >
        <Bookmark size={13} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
        {views.map((v) => {
          const on = isViewActive(v, active, sort);
          const t = toneStyles[v.tone] || toneStyles.default;
          return (
            <button
              key={v.id}
              type="button"
              aria-pressed={on}
              onClick={() => onApplyView(v)}
              style={{
                padding: '4px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit',
                background: on ? t.bg : 'transparent',
                color: on ? t.fg : 'var(--text-secondary)',
                border: `1px solid ${on ? t.border : 'var(--border-primary)'}`,
              }}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {FACETS.map((facet) => {
          const options = facet.options(labs);
          if (options.length === 0) return null;
          const counts = facetCounts(labs, facet, { active, search, now });
          const selected = active[facet.key] || [];

          return (
            <div
              key={facet.key}
              role="group"
              aria-label={`Filter by ${facet.label}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
            >
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.09em',
                textTransform: 'uppercase', color: 'var(--text-muted)',
                minWidth: 68, flex: 'none',
              }}>
                {facet.label}
              </span>
              {options.map((opt) => {
                const pressed = selected.includes(opt.value);
                const count = counts[opt.value] ?? 0;
                return (
                  <FilterPill
                    key={opt.value}
                    label={opt.label}
                    count={count}
                    pressed={pressed}
                    disabled={count === 0 && !pressed}
                    tone={opt.tone}
                    onToggle={() => onToggleFacet(facet.key, opt.value)}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-primary)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Announced to screen readers whenever the result count changes. */}
          <span role="status" aria-live="polite" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <strong style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
              {resultCount}
            </strong>
            {` of `}
            <strong style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
              {labs.length}
            </strong>
            {` labs`}
          </span>
          {activeCount > 0 && (
            <button
              type="button"
              className="btn"
              onClick={onClear}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 10px' }}
            >
              <X size={12} aria-hidden="true" />
              Clear {activeCount} filter{activeCount === 1 ? '' : 's'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
            <span>Group</span>
            <select
              value={groupBy}
              onChange={(e) => onGroupByChange(e.target.value)}
              aria-label="Group labs by"
              style={selectStyle}
            >
              {GROUP_BY.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
            <ArrowUpDown size={14} aria-hidden="true" />
            <span>Sort</span>
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value)}
              aria-label="Sort labs"
              style={selectStyle}
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>

          {/* Density switch. Cards suit a dozen labs; rows and compact suit a term's worth. */}
          <div role="group" aria-label="View density" style={{ display: 'flex', gap: 2, background: 'var(--bg-tertiary)', borderRadius: 7, padding: 2 }}>
            {VIEWS.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={view === value}
                title={label}
                onClick={() => onViewChange(value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '4px 9px',
                  borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 12,
                  fontFamily: 'inherit',
                  background: view === value ? 'var(--bg-secondary)' : 'transparent',
                  color: view === value ? 'var(--accent-primary)' : 'var(--text-muted)',
                }}
              >
                <Icon size={13} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
